/**
 * Per-row processing pipeline for the upload queue.
 *
 * Wraps the existing knowledge/documentUpload helpers so the queue's
 * worker pool can drive them through the queue's status states. The
 * factory shape lets the panel plug context (siteId / reportId) without
 * the queue itself knowing about SiteDocument shape.
 *
 * Stages:
 *   extracting  → extractTextFromFile
 *   summarizing → summarizeDocument (Gemini, with filename-fallback inside
 *                                    summarizeDocument itself)
 *   uploading   → saveDocument (POST /api/storage/save + upload-file +
 *                               Firestore metadata via authedFetch)
 *
 * Errors propagate to the queue's runOne, which handles attempts++ and
 * the failed → failed-permanent transition. Don't catch here.
 */
import type { SiteDocument } from '../../types/siteRag';
import type { ProcessRowFn } from './uploadQueue';

const MAX_TEXT_LENGTH = 10_000;

export interface UploadProcessorOptions {
  /** Site / report context — propagated to the SiteDocument so existing
   *  consumers that filter docs by parent stay happy. Optional today,
   *  required once the firestore schema lands a siteId index. */
  siteId?: string;
  reportId?: string;
  /** Override the underlying helpers for unit tests. */
  extractTextFromFile?: (file: File) => Promise<string>;
  summarizeDocument?: (text: string, fileName: string) => Promise<{ summary: string; tags: string[] }>;
  // Mirrors knowledge/documentUpload.saveDocument. Returns a structured
  // result (with an optional non-fatal `rawUploadWarning`); the queue
  // processor awaits but ignores it — the metadata write either resolves
  // or throws, which is all the queue's success/failure machinery needs.
  saveDocument?: (siteDoc: SiteDocument, rawFile?: File) => Promise<{ rawUploadWarning?: unknown } | void>;
}

export function createUploadProcessor(opts: UploadProcessorOptions = {}): ProcessRowFn {
  return async (row, file, { setStatus }) => {
    // ── extracting ────────────────────────────────────────────────
    await setStatus('extracting');
    const extractFn = opts.extractTextFromFile
      ?? (await import('../knowledge/documentUpload')).extractTextFromFile;
    const text = await extractFn(file);

    // ── summarizing ───────────────────────────────────────────────
    await setStatus('summarizing');
    const summarizeFn = opts.summarizeDocument
      ?? (await import('../knowledge/driveConnector')).summarizeDocument;
    const { summary, tags } = await summarizeFn(text, file.name);

    // Build the SiteDocument the persistence layer expects.
    const siteDoc: SiteDocument = {
      id: `doc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      summary,
      tags,
      textContent: text.slice(0, MAX_TEXT_LENGTH),
      uploadedAt: new Date().toISOString(),
    };

    // ── uploading ─────────────────────────────────────────────────
    await setStatus('uploading');
    const saveFn = opts.saveDocument
      ?? (await import('../knowledge/documentUpload')).saveDocument;
    await saveFn(siteDoc, file);

    return { docId: siteDoc.id };
  };
}
